import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cmsUpdateStudioText, loadMenuStudio, loadMenuStudioAsset, saveMenuStudio, saveMenuStudioOps } from "api";
import { contentIndex, generateDocument, validateDocument } from "./studioModel";
import { unpackArtwork } from "./studioAssets";
import { stampTouchedPages } from "./studioExportPages";
import { affectedPages, applyOps, diffDocs } from "./core/docOps";
import { isRef } from "./core/assets";
import { createDisplayCache, deriveDisplay, proposedRepairs, unresolvedLinks } from "./studioDisplay";
import { readRecovery, writeRecovery, writeConflictBackup } from "./studioRecovery";
import { resolveEdit, strip } from "./studioCommit";

// The Studio's document store (plan §2 and phase 2):
// - `authored` is the saved design; it changes only through commits.
// - `display` is authored plus layout derived from CMS text and fonts; it is
//   what the canvas and exports show, and it is never saved on its own.
// - a gesture preview is transient state; one committed operation ends it.
// - saves send small ID-addressed batches; each is idempotent by opId, and an
//   edit made during an in-flight save stays unsaved until acknowledged.
const HISTORY_LIMIT = 80;
const SAVE_DELAY = 800;
const MAX_BATCH_OPS = 4000;
const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
const reverseCms = (changes) => changes.map((c) => ({ ...c, changes: c.expected, expected: c.changes }));

// Storage format 2 sends artwork and fonts as refs; resolve them once per
// session into data URLs so editing, exports and recovery stay
// self-contained. Older backends return data URLs directly.
const assetCache = new Map();
async function resolveAssets(doc) {
  if (!doc) return doc;
  const refs = new Set();
  doc.pages.forEach((p) => p.elements.forEach((n) => { if (isRef(n.src)) refs.add(n.src); }));
  (doc.fonts || []).forEach((f) => { if (isRef(f.src)) refs.add(f.src); });
  if (!refs.size) return doc;
  await Promise.all([...refs].filter((r) => !assetCache.has(r)).map(async (r) => {
    assetCache.set(r, await loadMenuStudioAsset(r.slice(4)));
  }));
  const load = (v) => (isRef(v) ? assetCache.get(v) : v);
  const { format, ...rest } = doc;
  return {
    ...rest,
    pages: doc.pages.map((p) => ({ ...p, elements: p.elements.map((n) => (n.type === "image" && isRef(n.src) ? { ...n, src: load(n.src) } : n)) })),
    ...(doc.fonts ? { fonts: doc.fonts.map((f) => ({ ...f, src: load(f.src) })) } : {}),
  };
}

export default function useStudioDocument(menus, menuKey, initialDocuments) {
  const [authored, setAuthoredState] = useState(null);
  const [transient, setTransient] = useState(null);
  const [status, setStatus] = useState("Loading design…");
  const [error, setError] = useState("");
  const [fontsToken, setFontsToken] = useState(0);
  const [history, setHistoryValue] = useState({ past: [], future: [] });
  const [historyBusy, setHistoryBusy] = useState(false);
  const [recoveryState, setRecoveryState] = useState("idle");
  const authoredRef = useRef(null), historyRef = useRef(history);
  const revision = useRef(0), writable = useRef(false);
  const pending = useRef([]), inflight = useRef(null), seq = useRef(0);
  const opsApi = useRef(typeof saveMenuStudioOps === "function");
  const needsFullSave = useRef(false);
  const displayCache = useRef(createDisplayCache());
  const content = useMemo(() => contentIndex(menus), [menus]);
  const contentRef = useRef(content);
  contentRef.current = content;
  const setHistory = useCallback((change) => {
    const next = typeof change === "function" ? change(historyRef.current) : change;
    historyRef.current = next;
    setHistoryValue(next);
  }, []);
  const setAuthored = useCallback((next) => {
    authoredRef.current = next;
    setAuthoredState(next);
  }, []);
  const display = useMemo(() => deriveDisplay(authored, content, displayCache.current, fontsToken), [authored, content, fontsToken]);
  const displayRef = useRef(display);
  displayRef.current = display;
  const current = useRef(null);
  current.current = transient || display;
  const dirty = () => pending.current.length > 0 || needsFullSave.current || !!inflight.current;

  // --- load: never repairs, re-imports or rewrites an existing design ---
  useEffect(() => {
    let live = true;
    (async () => {
      let remote;
      try {
        remote = await loadMenuStudio(menuKey, { format: 2 });
        remote = { ...remote, doc: await resolveAssets(unpackArtwork(remote.doc)) };
        if (!live) return;
        revision.current = remote.revision;
        writable.current = true;
      } catch (_) {
        if (!live) return;
        setError("Cloud storage is unavailable. Your draft will be kept on this browser; use Save to retry or export a backup.");
      }
      const local = await readRecovery(menuKey).catch(() => null);
      if (!live) return;
      if (!remote && Number.isSafeInteger(local?.revision)) revision.current = local.revision;
      const remoteDoc = validateDocument(remote?.doc) ? remote.doc : null;
      let draft, recovered = false;
      if (validateDocument(local?.doc) && (local.dirty || !remoteDoc)) { draft = local.doc; recovered = true; }
      else draft = remoteDoc;
      if (!draft) {
        // Creating a missing design is the only save that happens on open. An
        // existing design is never replaced here, whatever its import flags;
        // Styles → Restore original print layout is the explicit way to do that.
        draft = initialDocuments?.[menuKey] || generateDocument(menus, menuKey);
        needsFullSave.current = true;
      }
      if (recovered) {
        const sameBase = Array.isArray(local.pending) && remote && local.revision === remote.revision;
        pending.current = sameBase ? local.pending.map((b) => ({ ...b, seq: ++seq.current })) : [];
        if (!pending.current.length && local.dirty) needsFullSave.current = true;
      }
      if (local?.dirty && remote && local.revision !== remote.revision) {
        writable.current = false;
        revision.current = Number.isSafeInteger(local.revision) ? local.revision : 0;
        setError("A newer cloud design exists. Your recovered draft is open. Export a backup or choose Use cloud version; undo will retain this draft.");
      }
      setAuthored(draft);
      setStatus(recovered ? "Draft recovered" : needsFullSave.current ? "Unsaved changes" : "All changes saved");
    })();
    return () => { live = false; };
    // Menu refreshes change the display only; the design loads once per menu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuKey, initialDocuments, setAuthored]);

  // Fonts change measured layout: re-derive the display once they are ready,
  // and again whenever a face finishes loading. The design's embedded faces
  // are declared by the canvas and only start loading after its first render,
  // so `ready` alone resolves before them and name marks stay placed for
  // fallback-font widths.
  const hasDocument = !!authored;
  useEffect(() => {
    if (!hasDocument) return;
    let live = true;
    const bump = () => { if (live) setFontsToken((t) => t + 1); };
    const fonts = typeof document !== "undefined" ? document.fonts : null;
    Promise.resolve(fonts?.ready).then(bump);
    fonts?.addEventListener?.("loadingdone", bump);
    return () => { live = false; fonts?.removeEventListener?.("loadingdone", bump); };
  }, [hasDocument]);

  // --- recovery: asynchronous and off the editing path ---
  const recoveryTimer = useRef(null);
  const persistRecovery = useCallback(() => {
    clearTimeout(recoveryTimer.current);
    recoveryTimer.current = setTimeout(async () => {
      if (!authoredRef.current) return;
      setRecoveryState("saving");
      try {
        await writeRecovery(menuKey, { doc: authoredRef.current, revision: revision.current, dirty: dirty(), pending: pending.current.map(({ ops }) => ({ ops })) });
        setRecoveryState("saved");
      } catch (_) {
        setRecoveryState("failed");
        setError("Browser recovery storage is full or unavailable. Save to the cloud or export a design backup before closing.");
      }
    }, 400);
  }, [menuKey]);

  // --- saving ---
  const save = useCallback(async () => {
    if (!authoredRef.current) return;
    if (inflight.current) return inflight.current;
    const run = (async () => {
      try {
        if (!writable.current) {
          const remote = await loadMenuStudio(menuKey, { format: 2 });
          if (remote.doc && remote.revision !== revision.current) {
            setError("The cloud design has changed. Export your draft as a backup before reopening the studio to resolve the conflict.");
            return;
          }
          revision.current = remote.revision;
          writable.current = true;
        }
        while ((pending.current.length || needsFullSave.current) && writable.current) {
          if (needsFullSave.current || revision.current === 0 || !opsApi.current) {
            const sentSeq = seq.current, doc = authoredRef.current;
            setStatus("Saving…");
            const result = await saveMenuStudio(menuKey, doc, revision.current);
            revision.current = result.revision;
            pending.current = pending.current.filter((b) => b.seq > sentSeq);
            // Edits made meanwhile are queued as operations on top of this save.
            needsFullSave.current = false;
            continue;
          }
          // The oldest batches go together; anything committed meanwhile
          // stays queued (and dirty) until its own acknowledgement.
          // A retried request resends exactly the same batches under the same
          // opId, so the server can recognise it as a duplicate.
          const head = pending.current[0];
          if (!head.opId) {
            let size = 0, count = 0;
            for (const b of pending.current) {
              if (size && count + b.ops.length > MAX_BATCH_OPS) break;
              size++; count += b.ops.length;
            }
            head.opId = `ed-${uid()}`;
            head.size = size;
          }
          const opId = head.opId;
          const sending = pending.current.slice(0, head.size);
          setStatus("Saving…");
          try {
            const result = await saveMenuStudioOps(menuKey, { baseRevision: revision.current, opId, ops: sending.flatMap((b) => b.ops), summary: [`${sending.length} edit${sending.length === 1 ? "" : "s"}`] });
            revision.current = result.revision;
            pending.current = pending.current.slice(sending.length);
          } catch (err) {
            const code = err.response?.status;
            if (code === 404 || code === 405) { opsApi.current = false; needsFullSave.current = true; continue; }
            throw err;
          }
        }
        if (writable.current) {
          setStatus(pending.current.length || needsFullSave.current ? "Unsaved changes" : "All changes saved");
          setError("");
        }
      } catch (err) {
        if (err.response?.status === 409) writable.current = false;
        setStatus("Saved on this browser");
        setError(err.response?.status === 409
          ? "Another session saved a newer version. Cloud saving is paused to protect both designs. Export this draft as a backup before resolving the conflict."
          : "Cloud save failed. Your browser draft is available; retry Save or export a design backup.");
      } finally {
        inflight.current = null;
        persistRecovery();
      }
    })();
    inflight.current = run;
    return run;
  }, [menuKey, persistRecovery]);

  const saveTimer = useRef(null);
  const scheduleSave = useCallback(() => {
    clearTimeout(saveTimer.current);
    if (!writable.current) return;
    saveTimer.current = setTimeout(() => { save(); }, SAVE_DELAY);
  }, [save]);
  useEffect(() => {
    if (hasDocument && needsFullSave.current && writable.current) scheduleSave();
  }, [hasDocument, scheduleSave]);

  // A committed change: authored update, history entry (ops + inverse).
  const record = useCallback((before, after, cms) => {
    const ops = diffDocs(before, after), inverse = diffDocs(after, before);
    if (!ops.length && !cms?.length) return null;
    setAuthored(after);
    setTransient(null);
    setHistory((h) => ({ past: [...h.past.slice(-(HISTORY_LIMIT - 1)), { ops, inverse, cms }], future: [] }));
    return { ops, inverse };
  }, [setAuthored, setHistory]);
  const enqueue = useCallback((ops) => {
    if (!ops.length) return;
    pending.current.push({ seq: ++seq.current, ops });
    setStatus("Unsaved changes");
    persistRecovery();
    scheduleSave();
  }, [persistRecovery, scheduleSave]);

  // Turn a change expressed on the displayed document into an authored one.
  // Only what the edit changed is carried over; derived layout elsewhere on
  // the page is not silently accepted.
  const resolveChange = useCallback((change, base) => {
    const prev = base || displayRef.current;
    if (!prev || !authoredRef.current) return null;
    let next = typeof change === "function" ? change(prev) : change;
    if (!next || next === prev) return null;
    try {
      return resolveEdit(prev, next, authoredRef.current, contentRef.current);
    } catch (err) {
      setError(`That edit could not be applied: ${err.message}`);
      return null;
    }
  }, []);

  const commit = useCallback((change, base) => {
    const resolved = resolveChange(change, base);
    setTransient(null);
    if (!resolved) return;
    const recorded = record(resolved.before, resolved.after);
    if (recorded) enqueue(recorded.ops);
  }, [resolveChange, record, enqueue]);

  // A change computed from the saved design itself (structure, shared
  // styles, repairs): diffed against authored, so derived layout elsewhere is
  // never swept into the save.
  const commitAuthored = useCallback((change) => {
    const before = authoredRef.current;
    if (!before) return;
    const next = typeof change === "function" ? change(before) : change;
    if (!next || next === before) return;
    const recorded = record(before, stampTouchedPages(before, next, affectedPages(before, diffDocs(before, next)), contentRef.current));
    if (recorded) enqueue(recorded.ops);
  }, [record, enqueue]);

  // A design change that must land together with CMS field changes. Resolves
  // with the updated CMS records only after both sides are saved.
  const commitWithCms = useCallback(async (change, cmsChanges) => {
    const resolved = resolveChange(change) || { before: authoredRef.current, after: authoredRef.current };
    if (opsApi.current && writable.current && revision.current > 0) {
      await save();
      if (pending.current.length || needsFullSave.current || !writable.current) throw new Error("Earlier changes have not saved yet. Retry once the design is saved.");
      const before = authoredRef.current;
      const after = before === resolved.before ? resolved.after : applyOps(before, strip(diffDocs(resolved.before, resolved.after))).doc;
      try {
        const result = await saveMenuStudioOps(menuKey, { baseRevision: revision.current, opId: `cms-${uid()}`, ops: diffDocs(before, after), cms: cmsChanges, summary: ["CMS text"] });
        revision.current = result.revision;
        record(before, after, cmsChanges);
        setStatus("All changes saved");
        persistRecovery();
        return result.cms || [];
      } catch (err) {
        const code = err.response?.status;
        if (code !== 404 && code !== 405) throw Object.assign(new Error(err.response?.data?.message || err.message), { response: err.response });
        opsApi.current = false;
      }
    }
    // Older backend: CMS first (optimistic check), then the design change.
    const results = [];
    for (const c of cmsChanges) results.push(await cmsUpdateStudioText(c));
    const recorded = record(resolved.before, resolved.after, cmsChanges);
    if (recorded) enqueue(recorded.ops);
    return results;
  }, [resolveChange, record, enqueue, save, menuKey, persistRecovery]);

  const preview = useCallback((next) => setTransient(next), []);

  // --- undo / redo: conditional, and CMS-coordinated when needed ---
  const traversing = useRef(false);
  const onCmsResults = useRef(null);
  const travel = useCallback(async (direction) => {
    if (traversing.current) return;
    const h = historyRef.current;
    const entry = direction === "undo" ? h.past[h.past.length - 1] : h.future[0];
    if (!entry) return;
    traversing.current = true;
    try {
      const before = authoredRef.current;
      const ops = direction === "undo" ? entry.inverse : entry.ops;
      const after = ops.length ? applyOps(before, ops).doc : before;
      const designOps = after === before ? [] : diffDocs(before, after);
      if (entry.cms?.length) {
        setHistoryBusy(true);
        const cms = direction === "undo" ? reverseCms(entry.cms) : entry.cms;
        let results = null;
        if (opsApi.current && writable.current && revision.current > 0) {
          await save();
          try {
            const result = await saveMenuStudioOps(menuKey, { baseRevision: revision.current, opId: `cms-${uid()}`, ops: designOps, cms, summary: [`${direction} CMS text`] });
            revision.current = result.revision;
            results = result.cms || [];
          } catch (err) {
            const code = err.response?.status;
            if (code !== 404 && code !== 405) throw err;
            opsApi.current = false;
          }
        }
        if (!results) {
          results = [];
          for (const c of cms) results.push(await cmsUpdateStudioText(c));
          if (designOps.length) pending.current.push({ seq: ++seq.current, ops: designOps });
        }
        onCmsResults.current?.(cms, results);
      } else if (designOps.length) {
        pending.current.push({ seq: ++seq.current, ops: designOps });
      }
      setHistory(direction === "undo"
        ? { past: h.past.slice(0, -1), future: [entry, ...h.future] }
        : { past: [...h.past, entry], future: h.future.slice(1) });
      setAuthored(after);
      setTransient(null);
      setStatus(pending.current.length || needsFullSave.current ? "Unsaved changes" : "All changes saved");
      persistRecovery();
      scheduleSave();
    } catch (err) {
      setError(err.name === "OpConflict"
        ? `This step can no longer be ${direction === "undo" ? "undone" : "redone"} because a later change touched the same element.`
        : err.response?.data?.message || "Could not update the CMS. Undo history is unchanged; retry when connected.");
    } finally {
      traversing.current = false;
      setHistoryBusy(false);
    }
  }, [menuKey, save, scheduleSave, persistRecovery, setAuthored, setHistory]);
  const undo = useCallback(() => travel("undo"), [travel]);
  const redo = useCallback(() => travel("redo"), [travel]);

  const useCloud = useCallback(async () => {
    if (inflight.current) return;
    try {
      const remote = await loadMenuStudio(menuKey, { format: 2 });
      const doc = await resolveAssets(unpackArtwork(remote.doc));
      if (!validateDocument(doc)) throw new Error("No cloud design is available.");
      const previous = authoredRef.current;
      await writeConflictBackup(menuKey, previous);
      // Undo returns to the draft, as a new edit on top of the cloud version.
      setHistory((h) => ({ past: [...h.past.slice(-(HISTORY_LIMIT - 1)), { ops: diffDocs(previous, doc), inverse: diffDocs(doc, previous) }], future: [] }));
      revision.current = remote.revision;
      writable.current = true;
      pending.current = [];
      needsFullSave.current = false;
      setAuthored(doc);
      setTransient(null);
      setStatus("All changes saved");
      setError("");
      persistRecovery();
    } catch (err) {
      setError(err.message || "Could not load the cloud design.");
    }
  }, [menuKey, setAuthored, setHistory, persistRecovery]);

  // Adopt a newer revision written by the server on the user's behalf (a CMS
  // deletion removing linked elements). Local work is saved first; the change
  // becomes one undo step, so undo puts the elements back.
  const syncFromCloud = useCallback(async () => {
    await save();
    if (pending.current.length || needsFullSave.current || !writable.current) return false;
    const remote = await loadMenuStudio(menuKey, { format: 2 });
    if (!remote?.doc || remote.revision === revision.current) return true;
    const doc = await resolveAssets(unpackArtwork(remote.doc));
    if (!validateDocument(doc)) return false;
    const before = authoredRef.current;
    revision.current = remote.revision;
    record(before, doc);
    setStatus("All changes saved");
    persistRecovery();
    return true;
  }, [menuKey, save, record, persistRecovery]);

  useEffect(() => {
    const warn = (e) => {
      if (pending.current.length || needsFullSave.current || inflight.current) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);
  useEffect(() => () => { clearTimeout(saveTimer.current); clearTimeout(recoveryTimer.current); }, []);

  // Structural repairs are proposals: listed with their scope, applied only
  // on request, undoable like any edit.
  const repairs = useMemo(() => (authored ? proposedRepairs(authored, content, { display }) : []), [authored, content, display]);
  const unresolved = useMemo(() => unresolvedLinks(authored, content), [authored, content]);
  const applyRepair = useCallback((id) => {
    const repair = repairs.find((r) => r.id === id);
    if (repair) commitAuthored(repair.next);
  }, [repairs, commitAuthored]);
  const getRevision = useCallback(() => revision.current, []);

  return {
    doc: transient || display,
    authored,
    current,
    commit,
    commitWithCms,
    commitAuthored,
    getRevision,
    preview,
    undo,
    redo,
    historyBusy,
    canUndo: !!history.past.length,
    canRedo: !!history.future.length,
    save,
    useCloud,
    syncFromCloud,
    status,
    error,
    setError,
    repairs,
    applyRepair,
    unresolved,
    recoveryState,
    onCmsResults,
  };
}
